/**
 * Read-only map of a commune's open sessions (`docs/ADMIN.md` §2 and §5):
 * the same Leaflet setup as `features/session/components/StartPointPicker`,
 * one marker per open session, a popup with its name, participants,
 * progress and quality. Every session has a pin, so every one is plotted.
 */
import * as L from 'leaflet';
import { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';

import { addBasemap } from '../../../components/map/basemap';

import { t } from '../../../i18n/fr';
import { formatQuality } from '../format';

import type { GeoPoint } from '../../../api/sessions';
import type { SessionMapEntry } from '../types';

export interface SessionsMapProps {
  /** Map centre on first render — the commune's centroid. */
  center: GeoPoint;
  /** Open sessions with a known start point (already filtered by the caller). */
  sessions: SessionMapEntry[];
}

/** A charter-styled marker: violet ring, yellow fill, no external image asset. */
function markerIcon(): L.DivIcon {
  return L.divIcon({
    className: 'admin-map__marker-wrapper',
    html: '<span class="admin-map__marker">✚</span>',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

/**
 * Build the popup's inner HTML for one session — plain strings only (no
 * user-authored content ever reaches this map), so `setContent` with a
 * built string is safe here.
 *
 * @param session - The session to describe.
 * @returns The popup's HTML.
 */
function popupHtml(session: SessionMapEntry): string {
  const participantsLabel =
    session.participants === 1
      ? t('admin.commune.map.participant')
      : t('admin.commune.map.participants', { count: session.participants });
  const qualityLabel =
    session.average_quality === null
      ? t('admin.commune.map.noQuality')
      : t('admin.commune.map.quality', { value: formatQuality(session.average_quality) });
  const progressLabel = t('admin.commune.map.progress', {
    recorded: session.progress.recorded,
    assigned: session.progress.assigned,
    remaining: session.progress.remaining,
  });
  return `<strong>${escapeHtml(session.name)}</strong><br/>${escapeHtml(participantsLabel)}<br/>${escapeHtml(progressLabel)}<br/>${escapeHtml(qualityLabel)}`;
}

/** Escape the handful of characters that matter in a popup's HTML string. */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/**
 * Render the read-only sessions map.
 *
 * @param props - Map centre and the sessions to plot.
 * @returns The map container element.
 */
export function SessionsMap({ center, sessions }: SessionsMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);

  // Mount the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return undefined;
    }
    const map = L.map(containerRef.current, {
      center: [center.latitude, center.longitude],
      zoom: 15,
    });
    addBasemap(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once
  }, []);

  // Keep the markers in sync with `sessions`.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    for (const marker of markersRef.current) {
      marker.remove();
    }
    markersRef.current = sessions.map((session) => {
      const marker = L.marker([session.latitude, session.longitude], { icon: markerIcon() }).addTo(
        map,
      );
      marker.bindPopup(popupHtml(session));
      return marker;
    });
    // Frame the sessions, not the commune's centre: a coordinator opens
    // this map to see where the pairs are, and a pin half off the edge is
    // a pin they will miss.
    if (sessions.length > 0) {
      map.fitBounds(
        L.latLngBounds(sessions.map((session) => [session.latitude, session.longitude])),
        { padding: [40, 40], maxZoom: 16 },
      );
    }
  }, [sessions]);

  return (
    <div
      ref={containerRef}
      className="admin-map"
      role="application"
      aria-label={t('admin.commune.map.mapLabel')}
    />
  );
}
